import React, { useMemo } from "react";
import DOMPurify from "dompurify";

/**
 * Safe HTML renderer.
 *
 * Sanitises admin-provided HTML with DOMPurify before injecting it into the
 * DOM, defeating XSS even when the admin panel is compromised. Forbids
 * `<script>`, inline event handlers (`onclick`, `onerror`, …), `javascript:`
 * URIs, and dangerous tags (form, iframe by default, etc.). Whitelisted tags
 * cover the formatting / linking needs of marketing copy.
 */
const ALLOWED_TAGS = [
  "h1", "h2", "h3", "h4", "h5", "h6",
  "p", "br", "hr", "blockquote", "pre",
  "strong", "em", "b", "i", "u", "s", "small", "mark", "code",
  "ul", "ol", "li",
  "a", "img",
  "table", "thead", "tbody", "tr", "th", "td",
  "div", "span", "section", "article",
];
const ALLOWED_ATTR = [
  "href", "title", "alt", "src", "target", "rel",
  "class", "id", "style",
  "width", "height",
  "colspan", "rowspan",
];

const sanitize = (dirty) =>
  DOMPurify.sanitize(dirty || "", {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
    FORBID_TAGS: ["script", "iframe", "object", "embed", "form", "input", "button", "style"],
    FORBID_ATTR: ["onerror", "onload", "onclick", "onmouseover", "onfocus", "onblur"],
    ALLOW_DATA_ATTR: false,
  });

export const SafeHtml = ({ html, as: Tag = "div", className, ...rest }) => {
  const clean = useMemo(() => sanitize(html), [html]);
  return (
    <Tag
      {...rest}
      className={className}
      dangerouslySetInnerHTML={{ __html: clean }}
    />
  );
};

export default SafeHtml;
